/** Shapes rows for the Tools About You page (browser-observable client state). */

export type VisitorInfoRow = { label: string; value: string; info: string }

export type VisitorInfoSection = { title: string; rows: VisitorInfoRow[] }

export type AboutYouClientSnapshot = {
  navigator: VisitorInfoSection
  viewportAndScreen: VisitorInfoSection
  localeAndTime: VisitorInfoSection
}

function str(v: unknown, fallback = ""): string {
  if (v == null || v === "") return fallback
  if (typeof v === "string") return v
  if (typeof v === "number" || typeof v === "boolean" || typeof v === "bigint") return String(v)
  try {
    return JSON.stringify(v)
  } catch {
    return fallback || String(v)
  }
}

export function buildAboutYouClientSnapshot(): AboutYouClientSnapshot {
  const nav = typeof navigator !== "undefined" ? navigator : undefined
  const scr = typeof screen !== "undefined" ? screen : undefined
  const tz = Intl.DateTimeFormat().resolvedOptions()

  return {
    navigator: {
      title: "Browser",
      rows: [
        {
          label: "userAgent",
          value: str(nav?.userAgent),
          info: "Identification string the browser reports. It is easy to change and is not a secure device ID.",
        },
        {
          label: "platform",
          value: str(nav?.platform),
          info: "Coarse operating system or hardware hint the browser may still surface for compatibility, even though newer guidance discourages relying on it.",
        },
        {
          label: "language",
          value: str(nav?.language),
          info: "Primary UI language tag the browser selects for this browsing context.",
        },
        {
          label: "languages",
          value: nav?.languages?.join(", ") ?? "",
          info: "Ordered list of preferred language tags the same way the browser reports them to pages.",
        },
        {
          label: "hardwareConcurrency",
          value: str(nav?.hardwareConcurrency),
          info: "Rough count of logical cores the browser is willing to use for parallel work in this environment.",
        },
        {
          label: "deviceMemory (GiB, if exposed)",
          value: str((nav as Navigator & { deviceMemory?: number }).deviceMemory),
          info: "Approximate device RAM in GiB when the browser exposes it. Often omitted for privacy.",
        },
        {
          label: "maxTouchPoints",
          value: str(nav?.maxTouchPoints),
          info: "Maximum touch points the browser reports. Zero usually means a pointer-first desktop profile, not a hard rule.",
        },
      ],
    },
    viewportAndScreen: {
      title: "Viewport and screen",
      rows: [
        {
          label: "window.inner width × height",
          value: `${window.innerWidth} × ${window.innerHeight}`,
          info: "Size of the layout viewport inside the browser chrome, measured in CSS pixels.",
        },
        {
          label: "devicePixelRatio",
          value: str(window.devicePixelRatio),
          info: "Ratio of physical screen pixels to one CSS pixel. Values above one are common on sharp or scaled displays.",
        },
        {
          label: "screen width × height",
          value: scr ? `${scr.width} × ${scr.height}` : "",
          info: "Total screen resolution the OS reports for this monitor or workspace (physical pixels, not the page).",
        },
        {
          label: "avail width × height",
          value: scr ? `${scr.availWidth} × ${scr.availHeight}` : "",
          info: "Screen space excluding areas reserved by the OS, such as taskbars or docks, when the browser exposes it.",
        },
      ],
    },
    localeAndTime: {
      title: "Locale and time",
      rows: [
        {
          label: "timezone",
          value: str(tz.timeZone),
          info: "Named region used when applying local dates and times in this browser (for example America or Europe city names).",
        },
        {
          label: "locale / numberingSystem",
          value: [tz.locale, tz.numberingSystem].filter(Boolean).join(", "),
          info: "Language and numbering conventions the browser applies when formatting text and numbers for this page.",
        },
      ],
    },
  }
}
